import {FormControl, TableRow, Option, Tooltip} from 'platform/components';
import {Box, Center, Link} from 'platform/foundation';
import {match} from 'ts-pattern';

import {useEffect} from 'react';
import {UseFormReturn, useWatch} from 'react-hook-form';

import {isNil} from 'ramda';
import {isNilOrEmpty, isNotNil, isNotString} from 'ramda-adjunct';

import {GetManufacturersResponse, useLazyCalculateFromWithoutVatQuery} from '@dms/api/core';
import {CountryAlpha3CodeEnum, VatRateType} from '@dms/api/shared';
import {useBulkPostCatalogNumberSuggestionsMutation} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {warehouseRoutes} from '@dms/routes';
import {handleApiError} from '@dms/shared';
import {getOptionsFromManufacturersWithSuggestions} from '@dms/shared-aftersales';

import {
  composePath,
  isNotNilOrEmpty,
  Nullish,
  precisionCalculation,
  pushGtmEvent,
  useDebouncedCallback,
  useDebouncedValue,
} from 'shared/web';

import {DelayEnum} from '../constants/DelayEnum';
import {DeliveryNoteDocumentPreviewFormDefinition} from '../types/DeliveryNoteDocumentPreviewFormDefinition';
import {DeliveryNoteField} from '../types/DeliveryNoteField';
import {PageItemCheckbox} from './PageItemCheckbox';
import {PageItemChoiceInput} from './PageItemChoiceInput';
import {PageItemCurrencyInput} from './PageItemCurrencyInput';
import {PageItemNumberInput} from './PageItemNumberInput';
import {PageItemTextInput} from './PageItemTextInput';

interface PageTableRowProps {
  item: DeliveryNoteField;
  isCollapsed: boolean;
  isConfirmed: boolean;
  isLoadingSuggestions: boolean;
  formApi: UseFormReturn<DeliveryNoteDocumentPreviewFormDefinition>;
  control: FormControl<DeliveryNoteDocumentPreviewFormDefinition>;
  countryCode: CountryAlpha3CodeEnum;
  manufacturers: GetManufacturersResponse | Nullish;
  handlingUnits: Option[];
  vatOptions: Option[];
}

export function PageTableRow(props: PageTableRowProps) {
  const [catalogNumberSuggestions, catalogNumber, lastAutoFilledCatalogNumber] = useWatch({
    control: props.control,
    name: [
      'catalogNumberSuggestions',
      `listOfItems.${props.item.originalIndex}.number`,
      `listOfItems.${props.item.originalIndex}.lastAutoFilledCatalogNumber`,
    ],
  });

  const debouncedCatalogNumber = useDebouncedValue(catalogNumber, DelayEnum.SUGGESTIONS);

  const handlePrefillArticleDetails = (manufacturerId: string | number) => {
    if (isNotString(manufacturerId)) {
      return;
    }

    if (isNil(catalogNumberSuggestions) || isNil(debouncedCatalogNumber)) {
      return;
    }

    const matchingSuggestion = catalogNumberSuggestions.find(
      (suggestion) => suggestion.catalogueNumber === debouncedCatalogNumber
    );

    if (isNil(matchingSuggestion) || isNil(matchingSuggestion.articles)) {
      return;
    }

    const articleWithManufacturer = matchingSuggestion.articles.find(
      (article) => article.manufacturer?.manufacturerId === manufacturerId
    );

    if (isNil(articleWithManufacturer)) {
      return;
    }

    const articleName = articleWithManufacturer.name;
    const handlingUnitId = articleWithManufacturer.measurementUnitId;
    const vatType = articleWithManufacturer.vatType;

    if (isNil(articleName) || isNil(handlingUnitId) || isNil(vatType)) {
      return;
    }

    props.formApi.setValue(`listOfItems.${props.item.originalIndex}.name`, articleName);
    props.formApi.setValue(`listOfItems.${props.item.originalIndex}.unit`, handlingUnitId);
    props.formApi.setValue(`listOfItems.${props.item.originalIndex}.vat`, vatType);
  };

  // Auto-sync manufacturer when catalog number changes and only one manufacturer is found in suggestions
  useEffect(() => {
    if (isNil(catalogNumberSuggestions) || isNil(debouncedCatalogNumber)) {
      return;
    }

    // Only auto-fill if catalog number has changed since the last auto-fill
    // This prevents re-applying auto-fill on remount while still allowing it on catalog number changes
    if (lastAutoFilledCatalogNumber === debouncedCatalogNumber) {
      return;
    }

    const matchingSuggestion = catalogNumberSuggestions.find(
      (suggestion) => suggestion.catalogueNumber === debouncedCatalogNumber
    );

    if (isNil(matchingSuggestion) || isNil(matchingSuggestion.articles)) {
      return;
    }

    const uniqueManufacturers = new Set(
      matchingSuggestion.articles
        .map((article) => article.manufacturer?.manufacturerId)
        .filter((id) => isNotNil(id))
    );

    if (uniqueManufacturers.size === 1) {
      const manufacturerId = Array.from(uniqueManufacturers)[0];

      props.formApi.setValue(
        `listOfItems.${props.item.originalIndex}.manufacturer`,
        manufacturerId
      );

      props.formApi.setValue(
        `listOfItems.${props.item.originalIndex}.lastAutoFilledCatalogNumber`,
        debouncedCatalogNumber
      );

      handlePrefillArticleDetails(manufacturerId);
    }
    // Disabling for props.formApi and props.item.originalIndex because these two are stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalogNumberSuggestions, debouncedCatalogNumber, lastAutoFilledCatalogNumber]);

  const [calculatePriceWithoutVat] = useLazyCalculateFromWithoutVatQuery();

  const [getCatalogNumberSuggestions, suggestionsStatus] =
    useBulkPostCatalogNumberSuggestionsMutation();

  const handlePriceCalculation = useDebouncedCallback(
    (fieldName: string, value: string | number) => {
      pushGtmEvent({event: 'button_click', event_id: 'ai_tool_dn_price_calculation'});

      const calculationParams = match(fieldName)
        .with(`listOfItems.${props.item.originalIndex}.quantity`, () => {
          const [unitPriceWithoutVat, vat] = props.formApi.getValues([
            `listOfItems.${props.item.originalIndex}.unitPrice`,
            `listOfItems.${props.item.originalIndex}.vat`,
          ]);

          const quantity = typeof value === 'number' ? value : null;

          if (isNil(quantity) || isNil(vat) || isNil(unitPriceWithoutVat)) {
            return null;
          }

          const totalPriceWithoutVat = precisionCalculation.round(
            precisionCalculation.multiply(quantity, unitPriceWithoutVat),
            2
          );

          return {
            unitPriceWithoutVat: precisionCalculation.round(unitPriceWithoutVat, 2),
            totalPriceWithoutVat,
            vat: vat as VatRateType,
          };
        })
        .with(`listOfItems.${props.item.originalIndex}.unitPrice`, () => {
          const [quantity, vat] = props.formApi.getValues([
            `listOfItems.${props.item.originalIndex}.quantity`,
            `listOfItems.${props.item.originalIndex}.vat`,
          ]);

          const unitPriceWithoutVat = typeof value === 'number' ? value : null;

          if (isNil(quantity) || isNil(vat) || isNil(unitPriceWithoutVat)) {
            return null;
          }

          const totalPriceWithoutVat = precisionCalculation.round(
            precisionCalculation.multiply(quantity, unitPriceWithoutVat),
            2
          );

          return {
            unitPriceWithoutVat: precisionCalculation.round(unitPriceWithoutVat, 2),
            totalPriceWithoutVat,
            vat: vat as VatRateType,
          };
        })
        .with(`listOfItems.${props.item.originalIndex}.totalPrice`, () => {
          const [quantity, vat] = props.formApi.getValues([
            `listOfItems.${props.item.originalIndex}.quantity`,
            `listOfItems.${props.item.originalIndex}.vat`,
          ]);

          const totalPriceWithoutVat = typeof value === 'number' ? value : null;

          if (isNil(quantity) || isNil(vat) || isNil(totalPriceWithoutVat)) {
            return null;
          }

          const unitPriceWithoutVat = precisionCalculation.round(
            precisionCalculation.divide(totalPriceWithoutVat, quantity),
            2
          );

          return {
            unitPriceWithoutVat,
            totalPriceWithoutVat: precisionCalculation.round(totalPriceWithoutVat, 2),
            vat: vat as VatRateType,
          };
        })
        .with(`listOfItems.${props.item.originalIndex}.vat`, () => {
          const [unitPriceWithoutVat, totalPriceWithoutVat] = props.formApi.getValues([
            `listOfItems.${props.item.originalIndex}.unitPrice`,
            `listOfItems.${props.item.originalIndex}.totalPrice`,
          ]);

          const vat = typeof value === 'string' ? value : null;

          if (isNil(vat) || isNil(unitPriceWithoutVat) || isNil(totalPriceWithoutVat)) {
            return null;
          }

          return {
            unitPriceWithoutVat: precisionCalculation.round(unitPriceWithoutVat, 2),
            totalPriceWithoutVat: precisionCalculation.round(totalPriceWithoutVat, 2),
            vat: vat as VatRateType,
          };
        })

        .otherwise(() => null);

      if (!calculationParams) {
        return;
      }

      if (
        calculationParams.unitPriceWithoutVat <= 0 ||
        calculationParams.totalPriceWithoutVat <= 0
      ) {
        return;
      }

      calculatePriceWithoutVat({
        amount: calculationParams.totalPriceWithoutVat.toString(),
        vatRateType: calculationParams.vat,
        countryCode: props.countryCode,
      })
        .unwrap()
        .then((result) => {
          props.formApi.setValue(
            `listOfItems.${props.item.originalIndex}.unitPrice`,
            precisionCalculation.round(calculationParams.unitPriceWithoutVat, 2)
          );

          props.formApi.setValue(
            `listOfItems.${props.item.originalIndex}.totalPrice`,
            precisionCalculation.round(Number(result.withoutVat.replace(',', '.')), 2)
          );
          props.formApi.setValue(
            `listOfItems.${props.item.originalIndex}.totalPriceWithVat`,
            precisionCalculation.round(Number(result.withVat.replace(',', '.')), 2)
          );
        })
        .catch(handleApiError);
    },
    DelayEnum.RECALCULATION
  );

  const handleCatalogNumberChange = useDebouncedCallback(
    async (fieldName: string, value: string | null) => {
      pushGtmEvent({event: 'button_click', event_id: 'ai_tool_dn_catalog_number_change'});

      if (fieldName !== `listOfItems.${props.item.originalIndex}.number`) {
        return;
      }

      if (isNil(value)) {
        return;
      }

      const hasSuggestionsForCatalogNumber = isNotNilOrEmpty(
        catalogNumberSuggestions?.find((suggestion) => suggestion.catalogueNumber === value)
      );

      if (hasSuggestionsForCatalogNumber) {
        return;
      }

      const supplierId = props.formApi.getValues('basicInformation.supplier');
      const warehouseId = props.formApi.getValues('basicInformation.warehouse');

      if (isNotString(supplierId)) {
        return;
      }

      if (isNotString(warehouseId)) {
        return;
      }

      await getCatalogNumberSuggestions({
        supplierId,
        warehouseId,
        searchForOtherSuppliers: true,
        searchInOtherWarehouses: true,
        catalogueNumbers: [value],
      })
        .unwrap()
        .then((result) => {
          if (isNilOrEmpty(result)) {
            return;
          }

          if (isNil(catalogNumberSuggestions)) {
            return;
          }

          const suggestions = result.filter((suggestion) => isNotNil(suggestion.articles));

          const mergedSuggestions = catalogNumberSuggestions.concat(suggestions);

          props.formApi.setValue(
            'catalogNumberSuggestions',
            mergedSuggestions.length > 0 ? mergedSuggestions : null
          );
        })
        .catch(handleApiError);
    },
    DelayEnum.SUGGESTIONS
  );

  const handleManufacturerChange = (fieldName: string, value: string | number) => {
    if (fieldName !== `listOfItems.${props.item.originalIndex}.manufacturer`) {
      return;
    }
    pushGtmEvent({event: 'button_click', event_id: 'ai_tool_dn_manufacturer_change'});
    handlePrefillArticleDetails(value);
  };

  return (
    <TableRow>
      <PageItemCheckbox
        index={props.item.originalIndex}
        isConfirmed={props.isConfirmed}
        control={props.control}
        name={`listOfItems.${props.item.originalIndex}.isSelected`}
      />
      <PageItemTextInput
        index={props.item.originalIndex}
        isCollapsed={props.isCollapsed}
        isConfirmed={props.isConfirmed}
        control={props.control}
        name={`listOfItems.${props.item.originalIndex}.number`}
        width={57}
        onChange={handleCatalogNumberChange}
      />
      <PageItemTextInput
        index={props.item.originalIndex}
        isCollapsed={props.isCollapsed}
        isConfirmed={props.isConfirmed}
        control={props.control}
        name={`listOfItems.${props.item.originalIndex}.name`}
        width={57}
      />
      <PageItemChoiceInput
        index={props.item.originalIndex}
        isCollapsed={props.isCollapsed}
        isConfirmed={props.isConfirmed}
        isLoadingOptions={props.isLoadingSuggestions || suggestionsStatus.isLoading}
        control={props.control}
        name={`listOfItems.${props.item.originalIndex}.manufacturer`}
        width={46}
        onChange={handleManufacturerChange}
        options={
          props.isLoadingSuggestions || suggestionsStatus.isLoading
            ? []
            : getOptionsFromManufacturersWithSuggestions(
                debouncedCatalogNumber,
                props.manufacturers,
                catalogNumberSuggestions
              )
        }
      />
      <PageItemNumberInput
        index={props.item.originalIndex}
        isCollapsed={props.isCollapsed}
        isConfirmed={props.isConfirmed}
        control={props.control}
        name={`listOfItems.${props.item.originalIndex}.quantity`}
        width={25}
        onChange={handlePriceCalculation}
      />
      <PageItemCurrencyInput
        index={props.item.originalIndex}
        isCollapsed={props.isCollapsed}
        isConfirmed={props.isConfirmed}
        control={props.control}
        name={`listOfItems.${props.item.originalIndex}.unitPrice`}
        width={35}
        onChange={handlePriceCalculation}
      />
      <PageItemCurrencyInput
        index={props.item.originalIndex}
        isCollapsed={props.isCollapsed}
        isConfirmed={props.isConfirmed}
        control={props.control}
        name={`listOfItems.${props.item.originalIndex}.totalPrice`}
        width={35}
        onChange={handlePriceCalculation}
      />
      <PageItemChoiceInput
        index={props.item.originalIndex}
        isCollapsed={props.isCollapsed}
        isConfirmed={props.isConfirmed}
        control={props.control}
        name={`listOfItems.${props.item.originalIndex}.unit`}
        width={25}
        options={props.handlingUnits}
      />
      <PageItemChoiceInput
        index={props.item.originalIndex}
        isCollapsed={props.isCollapsed}
        isConfirmed={props.isConfirmed}
        control={props.control}
        name={`listOfItems.${props.item.originalIndex}.vat`}
        width={25}
        options={props.vatOptions}
        onChange={handlePriceCalculation}
      />

      <Box paddingHorizontal={2}>
        <Tooltip
          isDisabled={!props.isConfirmed}
          label={i18n.t('entity.documentExtraction.actions.openInNewTab')}
          placement="top"
        >
          <Center>
            <Link
              isDisabled={!props.isConfirmed}
              leftIcon="action/open_in_new"
              href={composePath(warehouseRoutes.articleList, {
                queryParams: {catalogNumber: debouncedCatalogNumber},
              })}
              target="_blank"
              onClick={() => {
                pushGtmEvent({event: 'button_click', event_id: 'ai_tool_dn_row_action_click'});
              }}
            />
          </Center>
        </Tooltip>
      </Box>
    </TableRow>
  );
}
